import ButtonPrimary, {
  EButtonSize,
} from "@rentiohq/shared-frontend/dist/components/components/ButtonPrimary";
import PaymentRequestFetch from "@rentiohq/shared-frontend/dist/components/components/PaymentRequestFetch";
import PropertyFetchListItem from "@rentiohq/shared-frontend/dist/components/components/PropertyFetchListItem";
import * as paymentApi from "@rentiohq/shared-frontend/dist/redux/payment/payment.api";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { capitalize } from "@rentiohq/shared-frontend/dist/utils/text/text.utils";
import logger from "@rentiohq/shared/dist/logger";
import {
  EPaymentMethod,
  EPaymentRequestFeature,
  IPaymentRequest,
} from "@rentiohq/shared/dist/types/payment.types";
import { EInfoHeaderStyle } from "components/InfoHeader/InfoHeader.types";
import { WaitRender } from "components/WaitRender";
import { default as React } from "react";
import { useParams } from "react-router";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import withAuthenticationCheck from "../../../../hoc/withAuthenticationCheck";
import withKycRequired from "../../../../hoc/withKycRequired";
import PaymentRequestRedirect from "../../components/PaymentRequestRedirect";
import * as S from "./PaymentLimit.styles";

const PaymentLimitBase = () => {
  const params = useParams();

  const [pendingPressSelect, setPendingPressSelect] = React.useState(false);

  // Data
  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;
  const selectedPaymentMethod = params.paymentMethod;

  const callbackUrl = paymentRequestId
    ? `${window.origin}/payment/${paymentRequestId}/status`
    : undefined;

  // Event handlers
  const handlePressSelect = (paymentRequest: IPaymentRequest) => () => {
    if (!selectedPaymentMethod) {
      showAlert({
        type: "info",
        message: getLocalizedText(
          "payment.online.select_method.cta_info.select",
        ),
      });

      return;
    }

    if (!paymentRequestId) {
      return;
    }

    switch (selectedPaymentMethod) {
      case EPaymentMethod.Bancontact:
        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Bancontact,
          paymentRequest,
        });
        break;

      case EPaymentMethod.Ideal:
        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Ideal,
          paymentRequest,
        });
        break;

      default:
        break;
    }
  };

  const handlePressWebPayment = async (params: {
    paymentMethod: EPaymentMethod;
    paymentRequest: IPaymentRequest;
  }) => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { paymentMethod, paymentRequest } = params;

    setPendingPressSelect(true);

    try {
      // Legacy Mollie flow
      if (!paymentRequest.features.includes(EPaymentRequestFeature.BcmcV2)) {
        const { data } = await paymentApi.getMollieUrl(
          paymentRequestId,
          callbackUrl,
        );

        const { redirectUrl } = data;

        window.open(redirectUrl, "_self");
        return;
      }

      const { data } = await paymentApi.generateWebPayment(
        paymentRequestId,
        paymentMethod,
        callbackUrl,
      );

      const { redirectUrl } = data;

      if (!redirectUrl) {
        throw new Error("No redirectUrl or templateUrl found for web payment");
      }

      setTimeout(() => {
        window.open(redirectUrl, "_self");
      }, 20);
    } catch (unknownError) {
      const error = unknownError as any;
      logger.logError({ error });

      showAlert({ error, type: "error" });

      setPendingPressSelect(false);
    }
  };

  // Render
  const renderPaymentLimitMessage = (paymentRequest: IPaymentRequest) => {
    return (
      <PropertyFetchListItem
        renderAlways={() => {
          return renderMessage(paymentRequest);
        }}
      />
    );
  };

  const renderMessage = (paymentRequest: IPaymentRequest) => {
    return (
      <Page>
        <InfoHeader
          title={getLocalizedText("payment_limit_message.header")}
          titleStyle={EInfoHeaderStyle.Warning}
          body={getLocalizedText("payment_limit_message.body", {
            paymentMethod: capitalize(selectedPaymentMethod || "_"),
          })}
        />
        <WaitRender
          // key={`countdown_count-${this.state.countdownCount}`}
          timeout={15_000}
          withCountdown
          renderCountdown={secondsLeft => (
            <S.LisWrap>
              <S.ButtonPrimaryWrap>
                <ButtonPrimary
                  disabled={true}
                  title={`${getLocalizedText(
                    "payment_limit_message.button",
                  )} (${secondsLeft})`}
                  isLoading={pendingPressSelect}
                  onPress={handlePressSelect(paymentRequest)}
                  buttonSize={EButtonSize.Full}
                />
              </S.ButtonPrimaryWrap>
            </S.LisWrap>
          )}
        >
          <S.LisWrap>
            <S.ButtonPrimaryWrap>
              <ButtonPrimary
                title={getLocalizedText("payment_limit_message.button")}
                isLoading={pendingPressSelect}
                onPress={handlePressSelect(paymentRequest)}
                buttonSize={EButtonSize.Full}
              />
            </S.ButtonPrimaryWrap>
          </S.LisWrap>
        </WaitRender>
      </Page>
    );
  };

  if (!paymentRequestId || !selectedPaymentMethod) {
    return null;
  }

  return (
    <>
      <PaymentRequestRedirect id={paymentRequestId} />
      <PaymentRequestFetch
        id={paymentRequestId}
        renderPaymentRequest={renderPaymentLimitMessage}
      />
    </>
  );
};

export const PaymentLimit = withAuthenticationCheck(
  withKycRequired(PaymentLimitBase),
);
